'use client';

import { useState } from 'react';
import { X, Clock, Mail, User, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { FormInput } from './FormInput';
import { useTranslations } from 'next-intl';

const API_BASE_URL = '/api/proxy';

interface WaitlistModalProps {
  onClose: () => void;
  initialEmail?: string;
}

type ModalView = 'info' | 'form' | 'success';

export function WaitlistModal({ onClose, initialEmail = '' }: WaitlistModalProps) {
  const t = useTranslations('waitlistModal');
  const [view, setView] = useState<ModalView>('info');
  const [name, setName] = useState('');
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), source: 'login_page' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t('submitError'));
        return;
      }
      setView('success');
    } catch {
      setError(t('submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-in fade-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {view === 'info' && (
          <div className="flex flex-col items-center text-center gap-5 pt-2">
            <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center">
              <Clock className="w-7 h-7 text-orange-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-2">
                {t('infoTitle')}
              </h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                {t('infoDesc')}
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full">
              <button
                onClick={() => setView('form')}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm bg-orange-500 hover:bg-orange-600 text-white transition-all active:scale-[0.98]"
              >
                {t('joinBtn')} <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={onClose}
                className="w-full px-6 py-3 rounded-xl font-semibold text-sm text-slate-600 hover:bg-slate-50 transition-all"
              >
                {t('laterBtn')}
              </button>
            </div>
          </div>
        )}

        {view === 'form' && (
          <>
            <div className="mb-5">
              <h2 className="text-lg font-bold text-slate-900 mb-1">{t('formTitle')}</h2>
              <p className="text-sm text-slate-500">{t('formDesc')}</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <FormInput
                label={t('nameLabel')}
                icon={User}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('namePlaceholder')}
                required
                disabled={isSubmitting}
              />
              <FormInput
                label={t('emailLabel')}
                icon={Mail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('emailPlaceholder')}
                required
                disabled={isSubmitting}
                autoComplete="email"
              />
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setView('info')}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-semibold text-sm text-slate-700 bg-white hover:bg-slate-50 transition-all disabled:opacity-50"
                >
                  {t('backBtn')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm bg-orange-500 hover:bg-orange-600 text-white transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('submitBtn')}
                </button>
              </div>
            </form>
          </>
        )}

        {view === 'success' && (
          <div className="flex flex-col items-center text-center gap-5 pt-2">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle className="w-7 h-7 text-emerald-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-2">
                {t('successTitle')}
              </h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                {t('successDesc', { email })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-full px-6 py-3 rounded-xl font-semibold text-sm bg-teal-600 hover:bg-teal-700 text-white transition-all active:scale-[0.98]"
            >
              {t('closeBtn')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
